import type { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, MockFrame, Section, SectionHeading } from "./primitives";
import { TxRow } from "./mock";

/** A radio as Fio's form draws it, for the token mockup. */
function Radio({ on, children }: { on?: boolean; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2">
      <span
        className={
          on
            ? "flex size-3.5 items-center justify-center rounded-full border-2 border-[hsl(var(--action))] dark:border-primary"
            : "size-3.5 rounded-full border-2 border-muted-foreground/40"
        }
      >
        {on && <span className="size-1.5 rounded-full bg-[hsl(var(--action))] dark:bg-primary" />}
      </span>
      <span className={on ? "text-foreground" : "text-muted-foreground"}>{children}</span>
    </span>
  );
}

/** The core setup: a Fio API token, pasted once, then a daily sync. Always a dark band. */
export function HowItWorksSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  const steps: { title: string; line: string; label: string; mock: ReactNode }[] = [
    {
      title: tr("Ve Fio vytvoříte token", "Create a token at Fio"),
      line: tr(
        "Jen pro sledování účtu, bez práva platit.",
        "Account monitoring only, no right to pay."
      ),
      label: tr(
        "Formulář nového API tokenu ve Fio s oprávněním Sledování účtu",
        "Fio's new API token form with the Account monitoring permission"
      ),
      mock: (
        <div className="space-y-3 p-4 text-[13px]">
          <p className="text-xs font-medium text-muted-foreground">
            {tr("Fio · Nastavení → API", "Fio · Settings → API")}
          </p>
          <div className="space-y-2 rounded-lg border p-3">
            <Radio on>{tr("Sledování účtu", "Account monitoring")}</Radio>
            <Radio>{tr("Zadávání plateb", "Sending payments")}</Radio>
          </div>
          <div className="flex items-center justify-between rounded-lg border px-3 py-2">
            <span>{tr("Automatické prodlužování", "Automatic prolongation")}</span>
            <span className="flex h-4 w-7 items-center justify-end rounded-full bg-[hsl(var(--action))] p-0.5 dark:bg-primary">
              <span className="size-3 rounded-full bg-white" />
            </span>
          </div>
        </div>
      ),
    },
    {
      title: tr("Vložíte ho do Zettlu", "Paste it into Zettl"),
      line: tr("Korunový a eurový účet přidáte zvlášť.", "Add CZK and EUR accounts separately."),
      label: tr(
        "Nastavení Zettlu s připojenými účty Fio",
        "Zettl settings with connected Fio accounts"
      ),
      mock: (
        <div className="space-y-3 p-4 text-[13px]">
          <p className="text-xs font-medium text-muted-foreground">
            {tr("Zettl · Banky a integrace", "Zettl · Banks & integrations")}
          </p>
          <div className="flex gap-2">
            <span className="flex h-8 min-w-0 flex-1 items-center rounded-md border bg-background px-2.5 font-mono text-xs tracking-wider text-muted-foreground">
              ••••••••••••••••3f9a
            </span>
            <span className="flex h-8 items-center rounded-md bg-[hsl(var(--action))] px-3 text-xs font-medium text-white dark:bg-primary dark:text-primary-foreground">
              {tr("Připojit", "Connect")}
            </span>
          </div>
          <div className="divide-y rounded-lg border">
            {[
              tr("Fio · korunový účet", "Fio · CZK account"),
              tr("Fio · eurový účet", "Fio · EUR account"),
            ].map((name) => (
              <div key={name} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="break-words">{name}</span>
                <DotChip color="green">{tr("Připojeno", "Connected")}</DotChip>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: tr("Platby chodí každé ráno", "Payments arrive every morning"),
      line: tr(
        "Zettl je stáhne a hned spáruje s doklady.",
        "Zettl downloads them and matches them right away."
      ),
      label: tr(
        "Ranní stažení plateb z Fio: nové platby se stavem párování",
        "The morning Fio sync: new payments with their matching state"
      ),
      mock: (
        <div className="text-[13px]">
          <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-4">
            <p className="text-xs font-medium text-muted-foreground">
              {tr("Dnes 6:00 · 3 nové platby", "Today 6:00 · 3 new payments")}
            </p>
            <RefreshCw
              className="size-3.5 text-[hsl(var(--action))] dark:text-primary"
              strokeWidth={2}
            />
          </div>
          <div className="divide-y border-t">
            <TxRow
              locale={locale}
              tx={{
                date: "23. 10.",
                text: { cs: "Kavárna Na Rohu", en: "Kavárna Na Rohu" },
                amount: "−186,00 Kč",
                status: "matched",
              }}
            />
            <TxRow
              locale={locale}
              tx={{
                date: "22. 10.",
                text: { cs: "Studio Lípa · faktura", en: "Studio Lípa · invoice" },
                amount: "24 200,00 Kč",
                status: "invoice",
              }}
            />
            <TxRow
              locale={locale}
              tx={{
                date: "22. 10.",
                text: { cs: "Webhosting Oblak", en: "Oblak web hosting" },
                amount: "−349,00 Kč",
                status: "unmatched",
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Section id="jak-to-funguje" labelledBy="how-title" tone="tinted">
      <Container>
        <SectionHeading
          id="how-title"
          eyebrow={tr("Jak to funguje", "How it works")}
          title={tr("Napojení na Fio banku nastavíte jednou", "Connect Fio banka once")}
          lead={tr(
            "Stačí token pro sledování účtu, žádné heslo do banky.",
            "All it takes is an account-monitoring token, never your banking password."
          )}
        />
        <ol className="mt-12 grid gap-8 md:grid-cols-3 md:gap-6">
          {steps.map((step, n) => (
            <li key={step.title} className="flex flex-col">
              <MockFrame label={step.label} className="flex-1 bg-card">
                {step.mock}
              </MockFrame>
              <div className="mt-5 flex gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-background text-[13px] font-semibold tabular-nums text-[hsl(var(--action))] dark:text-primary">
                  {n + 1}
                </span>
                <div>
                  <h3 className="text-base font-semibold leading-tight tracking-tight">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {step.line}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Container>
    </Section>
  );
}
